// Generated by TaroBase SDK Generator: 2026-04-14T11:42:05.668Z
// Collection: presetStickers/$stickerId
// Edits may be overwritten by subsequent runs!

import {
  get, getMany, set, subscribe, getFiles, setFile, runQuery, count, aggregate, transformValues,
  Time, Token, Increment, Address, PublicKey,
  TimeOperation, IncrementOperation, TokenAmount, AddressType,
  TarobaseMetadata, DocumentOperation, FileItem, setMany, formatError,
} from '../db-client';
import type { AggregateResult, AggregateOperation } from '../db-client';

/**
 * Preset sticker images for Danger Room chat. Team members (admin, founder, or anyone in the teamMembers collection) can add, reorder, and remove stickers. All users can view stickers to use them in chat messages.
 */

export interface PresetStickersRequest {
  imageUrl: string;
  name: string;
  order: number | TimeOperation | IncrementOperation | TokenAmount;
  addedBy: string;
  addedAt: number | TimeOperation | IncrementOperation | TokenAmount;
}

export interface PresetStickersResponse {
  imageUrl: string;
  name: string;
  order: number;
  addedBy: string;
  addedAt: number;
  id: string;
  tarobase_created_at: number;
}

/**
 * Build a PresetStickers operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildPresetStickers(stickerId: string, data: PresetStickersRequest): DocumentOperation {
  const doc = transformValues(data);
  return { path: `presetStickers/${stickerId}`, document: doc };
}

/**
 * Admin, founder, or any team member (wallet exists in teamMembers collection) can add a preset sticker. imageUrl, name, addedBy, and addedAt are immutable after creation. addedBy should be set to the caller's wallet address. addedAt is a Unix seconds timestamp. (Create/Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the write worked.
 */
export async function setPresetStickers(stickerId: string, data: PresetStickersRequest): Promise<boolean> {
  try {
    const transformedData = transformValues(data);
    const succeeded = await set(`presetStickers/${stickerId}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error setting PresetStickers: ${formatError(error)}`);
    return false;
  }
}

export type PresetStickersRequestUpdate = Partial<PresetStickersRequest>;

/**
 * Build a PresetStickers update operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildUpdatePresetStickers(stickerId: string, data: PresetStickersRequestUpdate): DocumentOperation {
  const doc = transformValues(data);
  return { path: `presetStickers/${stickerId}`, document: doc };
}

/**
 * Admin, founder, or any team member can update. Only the order field can be changed (for reordering stickers in the picker). imageUrl, name, addedBy, and addedAt are immutable. (Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the update worked.
 */
export async function updatePresetStickers(stickerId: string, data: PresetStickersRequestUpdate): Promise<boolean> {
  try {
    const transformedData = transformValues(data);
    const succeeded = await set(`presetStickers/${stickerId}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error updating PresetStickers: ${formatError(error)}`);
    return false;
  }
}

/**
 * 
  Read Operation Details: Anyone can read preset stickers. Used to display available stickers in the sticker picker UI for Danger Room chat.
   (Get Single Item)
 */
export async function getPresetStickers(stickerId: string): Promise<PresetStickersResponse | null> {
  try {
    const result = await get(`presetStickers/${stickerId}`);
    return result as PresetStickersResponse | null;
  } catch (error) {
    console.error(`Error getting PresetStickers: ${formatError(error)}`);
    return null;
  }
}

/**
 * Subscribes to changes in a single PresetStickers document. (
  Read Operation Details: Anyone can read preset stickers. Used to display available stickers in the sticker picker UI for Danger Room chat.
  )
 */
export function subscribePresetStickers(callback: (data: PresetStickersResponse | null) => void, stickerId: string): Promise<() => Promise<void>> {
  return subscribe(`presetStickers/${stickerId}`, { // Use the item's basePath
    onData: (data) => {
      callback(data as PresetStickersResponse | null);
    },
    onError: (error) => {
      console.error(`Error subscribing to PresetStickers at path '${`presetStickers/${stickerId}`}': ${formatError(error)}`);
    }
  });
}

/**
 * Get many PresetStickers items from collection presetStickers
 
  Read Operation Details: Anyone can read preset stickers. Used to display available stickers in the sticker picker UI for Danger Room chat.
  
 */
export async function getManyPresetStickers(filter: string = ""): Promise<PresetStickersResponse[]> {
  try {
    const results = await get("presetStickers", { prompt: filter });
    return Array.isArray(results) ? results as PresetStickersResponse[] : [];
  } catch (error) {
    console.error(`Error getting PresetStickers collection at path 'presetStickers': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in PresetStickers collection at presetStickers
 
  Read Operation Details: Anyone can read preset stickers. Used to display available stickers in the sticker picker UI for Danger Room chat.
  
 */
export function subscribeManyPresetStickers(callback: (data: PresetStickersResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("presetStickers", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as PresetStickersResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to PresetStickers collection at path '${"presetStickers"}': ${formatError(error)}`);
    }
  });
}

/**
 * Get all PresetStickers items from collection presetStickers
 
  Read Operation Details: Anyone can read preset stickers. Used to display available stickers in the sticker picker UI for Danger Room chat.
  
 */
export async function getAllPresetStickers(filter: string = ""): Promise<PresetStickersResponse[]> {
  try {
    const results = await get("presetStickers", { prompt: filter });
    return Array.isArray(results) ? results as PresetStickersResponse[] : [];
  } catch (error) {
    console.error(`Error getting PresetStickers collection at path 'presetStickers': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in PresetStickers collection at presetStickers
 
  Read Operation Details: Anyone can read preset stickers. Used to display available stickers in the sticker picker UI for Danger Room chat.
  
 */
export function subscribeAllPresetStickers(callback: (data: PresetStickersResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("presetStickers", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as PresetStickersResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to PresetStickers collection at path '${"presetStickers"}': ${formatError(error)}`);
    }
  });
}

/**
 * Count PresetStickers items in collection presetStickers.
 * Returns a numeric count instead of fetching full documents — much more efficient.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param filter - Optional natural language filter (e.g., "created in the last 7 days")
 * @returns AggregateResult with the count value
 */
export async function countPresetStickers(filter: string = ""): Promise<AggregateResult> {
  try {
    return await count("presetStickers", { prompt: filter || undefined });
  } catch (error) {
    console.error(`Error counting PresetStickers at path 'presetStickers': ${formatError(error)}`);
    return { value: 0 };
  }
}

/**
 * Run an aggregate operation on PresetStickers items in collection presetStickers.
 * Returns a single numeric value instead of fetching full documents.
 *
 * Supported operations: 'count', 'uniqueCount', 'sum', 'avg', 'min', 'max'.
 * For 'sum', 'avg', 'min', 'max', and 'uniqueCount', you must provide the field name.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param operation - The aggregate operation to perform
 * @param opts - Options including optional filter prompt and field name
 * @returns AggregateResult with the computed numeric value
 */
export async function aggregatePresetStickers(operation: AggregateOperation, opts?: { filter?: string; field?: string }): Promise<AggregateResult> {
  try {
    return await aggregate("presetStickers", operation, { prompt: opts?.filter || undefined, field: opts?.field });
  } catch (error) {
    console.error(`Error aggregating PresetStickers at path 'presetStickers': ${formatError(error)}`);
    return { value: 0 };
  }
}

/**
 * 
  Delete Operation Details: Admin, founder, or any team member can remove a sticker from the preset collection.
   (Delete Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the delete worked.
 */
export async function deletePresetStickers(stickerId: string): Promise<boolean> {
  try {
    const succeeded = await set(`presetStickers/${stickerId}`, null);
    return !!succeeded;
  } catch (error) {
    console.error(`Error deleting PresetStickers: ${formatError(error)}`);
    return false;
  }
}

/**
 * Build a delete operation for PresetStickers for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildDeletePresetStickers(stickerId: string): DocumentOperation {
  return { path: `presetStickers/${stickerId}`, document: null };
}

